"use client";

import { GotoRegister } from "@/app/[lang]/login/goto.register";
import { useTranslation } from "@/i18n/client";
import { captureException } from "@tucan/utils/error-tracking";
import { getPublicEnv } from "@tucan/utils";
import { LoginForm, type LoginFormTexts, type LoginMethod } from "@tucan/login";
import { App } from "antd";
import { useGoogleLoginTexts } from "components/auth/login.with.google.button";
import { useLoginApi } from "hooks/use.login.api";
import { useRegisterActivity } from "hooks/use.register.activity";
import { useRouter } from "next/navigation";
import { Suspense, useEffect, useSyncExternalStore } from "react";
import { Trans } from "react-i18next";
import { useAppDispatch } from "store";
import { triggerLogin } from "store/auth";

/** Tries localStorage; the error when the browser refuses it, else `null`. */
const storageError = (): unknown => {
  try {
    localStorage.setItem("test-field", "test-value");
    localStorage.removeItem("test-field");
    return null;
  } catch (e) {
    return e;
  }
};

const isStorageAvailable = () => storageError() === null;
const noSubscription = () => () => undefined;

/**
 * Whether the browser lets the app use localStorage, which the signed-in app needs. The server
 * render assumes it does; when it does not, a toast says so and the error is reported.
 */
const useStorageAvailable = (): boolean => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const available = useSyncExternalStore(noSubscription, isStorageAvailable, () => true);

  useEffect(() => {
    if (available) {
      return;
    }
    void message.error(
      t("local-storage-unavailable", "No local storage available. Cannot login"),
    );
    captureException(storageError());
  }, [available, message, t]);

  return available;
};

/** The ui's sign-in page: `@tucan/login`'s `LoginForm` with the ui's texts and side effects. */
export const Login = () => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const registerActivity = useRegisterActivity();
  const storageAvailable = useStorageAvailable();
  const api = useLoginApi();
  const googleTexts = useGoogleLoginTexts();
  const googleClientId = getPublicEnv("GOOGLE_CLIENT_ID");

  const texts: LoginFormTexts = {
    backToLogin: t("reset-unsupported-challenge", "Back to Login"),
    emailInvalid: t("invalid-email", "not a valid email"),
    emailLabel: "Email",
    emailPlaceholder: t("email-placeholder", "please enter your email"),
    emailRequired: t("email-required", "email required"),
    emailSent: t("email-sent-successfully", "E-Mail sent, check your inbox!"),
    invalidRedirect: "Invalid Redirect Path",
    loggedIn: t("you-logged-in", "You are now logged in!"),
    passwordExpired: t(
      "password-login-expired-renew",
      "Your password expired, please set a new one.",
    ),
    passwordLabel: "Password",
    passwordLoginFailed: t(
      "password-login-failed",
      "There is something wrong with your password.",
    ),
    passwordRequired: t("password-required", "password required"),
    passwordSubmit: t("login-check-password", "Login"),
    passwordTitle: t("login-challenge-password", "Please enter your password"),
    requestFailed: t("login-request-failed", "Login failed, please try again."),
    sendEmailLinkInstead: t(
      "login-forgot-password",
      "Send me an email login link instead",
    ),
    submit: t("sign-in"),
    title: t("member-login"),
    unsupportedChallenge: t("login-unsupported-challenge", "Unsupported challenge"),
    userNotFound: t(
      "user-load-failed",
      "Could not find user, do you want to register?",
    ),
  };

  const onLoggedIn = (method: LoginMethod) => {
    if (method === "google") {
      dispatch(triggerLogin());
    }
    void registerActivity("LOGIN", undefined, { with: method });
    router.push(`/${i18n.language}/app`);
  };

  return (
    <LoginForm
      api={api}
      texts={texts}
      pendingPath="/login/pending"
      changePasswordPath="/login/change-password"
      onLoggedIn={onLoggedIn}
      onEvent={(event) =>
        void registerActivity(event === "identify" ? "LOGIN_IDENTIFY" : "LOGIN_SENT")
      }
      onError={captureException}
      google={googleClientId ? { clientId: googleClientId, texts: googleTexts } : undefined}
      zitadel={{}}
      disabled={!storageAvailable}
      notice={
        <p
          style={{
            color: "#B9B9B9",
            fontSize: 12,
            lineHeight: "20px",
            marginBottom: 16,
            marginLeft: "auto",
            marginRight: "auto",
            maxWidth: "80%",
            textAlign: "center",
          }}
        >
          <Trans i18nKey={"login-recaptcha"}>
            1
            <a href={"/tos"} target={"_blank"} rel="noopener noreferrer">
              2
            </a>{" "}
            3
            <a
              href={getPublicEnv("PRIVACY_AGREEMENT_URL")}
              target={"_blank"}
              rel="noopener noreferrer"
            >
              4
            </a>
            5
          </Trans>
        </p>
      }
      footer={
        <Suspense>
          <GotoRegister />
        </Suspense>
      }
    />
  );
};
